import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { trustBadges } from '@/mocks/seminarData';

const BADGE_COUNT = 3;

export default function HeroSection() {
  const { t } = useTranslation();
  const textRef = useRef<HTMLDivElement>(null);
  const [badgesVisible, setBadgesVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (textRef.current) {
        textRef.current.classList.add('opacity-100');
        textRef.current.classList.remove('opacity-0', 'translate-y-8');
      }
    }, 200);

    const badgeTimer = setTimeout(() => {
      setBadgesVisible(true);
    }, 600);

    return () => {
      clearTimeout(timer);
      clearTimeout(badgeTimer);
    };
  }, []);

  return (
    <>
    <section className="hero-billboard relative h-[60vh] min-h-[680px] md:h-[82vh] md:min-h-[640px] overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="/images/generated/seminar-hero-v2-2026.jpg"
          alt={t('seminar.hero.imgAlt')}
          title={t('seminar.hero.imgTitle')}
          className="w-full h-full object-cover object-top"
        />
      </div>

      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-primary-900/60 via-primary-900/45 to-primary-900/65"></div>

      {/* Content */}
      <div className="relative z-10 h-full flex items-center pb-44 sm:pb-0">
        <div className="max-w-[1600px] mx-auto px-8 md:px-12 lg:px-16 w-full">
          <div
            ref={textRef}
            className="max-w-2xl opacity-0 translate-y-8 transition-all duration-1000 ease-out"
          >


            {/* Main Title */}
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl text-foreground-50 leading-tight tracking-[0.06em] mb-5">
              {t('seminar.hero.title')}
            </h1>

            {/* English Subtitle */}
            {String(t('seminar.hero.titleEn')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-accent-400/70 text-lg md:text-xl tracking-[0.15em] mb-6">{__s}</p>))}

            {/* Lead */}
            {String(t('seminar.hero.lead')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-foreground-300/80 text-[17px] leading-relaxed tracking-[0.04em] mb-0 max-w-lg">{__s}</p>))}
          </div>
        </div>
      </div>

      {/* Trust Badges */}
      <div
        className={[
          'absolute bottom-0 left-0 right-0 bg-primary-900/80 backdrop-blur-sm border-t border-white/8 transition-all duration-700',
          badgesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4',
        ].join(' ')}
      >
        <div className="max-w-[1600px] mx-auto px-8 md:px-12 lg:px-16 py-4 flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-12">
          {Array.from({ length: BADGE_COUNT }, (_, index) => (
            <div key={index} className="flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center">
                <i className={[trustBadges[index].icon, 'text-accent-400 text-lg'].join(' ')}></i>
              </span>
              <span className="text-[14px] text-foreground-200 tracking-[0.05em] text-center sm:whitespace-nowrap">
                {t(`seminar.overview.badge.${index}`)}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-[72px] left-1/2 -translate-x-1/2 hidden sm:flex flex-col items-center gap-2 animate-bounce">
        <span className="text-[12px] text-foreground-400 tracking-[0.15em]">SCROLL</span>
        <div className="w-[1px] h-6 bg-gradient-to-b from-accent-500/30 to-transparent"></div>
      </div>
    </section>
    <nav aria-label="パンくずリスト" className="bg-background-50 border-b border-background-200"><div className="max-w-[1600px] mx-auto px-8 md:px-12 lg:px-16 py-3 flex items-center gap-2 text-[13px] text-foreground-500"><Link to="/" className="hover:text-accent-600 transition-colors">{t('seminar.hero.homeLink')}</Link><i className="ri-arrow-right-s-line text-foreground-400 text-[12px]"></i><Link to="/admission" className="hover:text-accent-600 transition-colors">{t('seminar.hero.admissionLink')}</Link><i className="ri-arrow-right-s-line text-foreground-400 text-[12px]"></i><span className="text-foreground-700">{t('seminar.hero.currentPage')}</span></div></nav>
    </>
  );
}
